import { FC, MutableRefObject, useEffect, useRef, useState } from 'react';
import {
  AreaResponse,
  IndustryLResponse,
  JobCountResponse,
  JobTypeLResponse,
  SearchConditionLabelResponse,
} from '@doda/api-client/aspida/@types';
import { getErrorScreenPath } from '@doda/common/src/logics/error';
import { CompanyOfSearchModal } from '@doda/common/src/models/MasterCacheModel';
import { Breadcrumb, BreadcrumbItem } from '@doda/io-react/pc';
import { BreadcrumbItemTop } from 'components/BreadcrumbItemTop';
import { WithoutNavigation } from 'components/FollowSearchResultsNavigation/WithoutNavigation';
import { Box } from '@doda/io-react/common';
import { useCountUpdate } from '@doda/common/src/hooks/useCountUp';
import { SearchConditionSettingForm } from 'components/SearchConditionSettingForm';
import { useDisclosure } from '@doda/common/src/hooks/useDisclosure';
import { ModalOverlay } from 'components/ModalOverlay';
import { OverwriteConditionModal } from 'components/OrverwriteConditionModal';
import { SearchSelectiveSettingForm } from 'components/SearchSelectiveSettingForm';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { adobeAnalyticsScriptsSelector } from '@doda/common/src/redux/features/analytics';
import { useAnalyticsSetup } from '@doda/common/src/hooks/useAnalytics';
import { Tag } from '@doda/common/src/components/Head/metaTag';
import {
  CANONICAL_URL_JOB_SEARCH_TOP,
  OGP_IMAGE,
  OGP_SITE,
  OGP_TYPE,
  OGP_URL_JOB_SEARCH_TOP,
} from '@doda/common/src/constants/properties/arca';
import { ConditionLinkList } from 'components/ConditionLinkList';
import { useCmsMemberInfo } from '@doda/common/src/hooks/useCmsMemberInfo';
import { assignPrefIdList } from '@doda/common/src/logics/masterCache';
import { createSaveConditionsRequest } from 'logics/createSaveConditionsRequest';
import { useSearchCondition } from '../../hooks/useSearchCondition';
import styles from './index.module.scss';
import { JobSearchTopHeader } from '../../components/JobSearchTopHeader';
import { PreviousCondition } from '../../components/PreviousCondition';
import { ValidationErrorText } from '../../components/ValidationErrorText';
import { SavedButton } from '../../components/SavedButton';
import { SearchButton } from '../../components/SearchButton';
import { moveToSearchResultPage } from '../../logics/moveToSearchResultPage';
import {
  measureOnClickLocation,
  measureOnClickStation,
} from '../../logics/analytics';
import { ValidationErrorType } from '../../interface';
import { onClickSavedCondition } from '../../logics/onClickSavedCondition';
import { InitialConditionsType } from '../../logics/createInitialCheckedList';

/**
 * JobSearchTopPropsの定義
 *  @param isLogin ログインフラグ
 *  @param jobCount 求人のファセット件数
 *  @param linkAreaHtml リンクエリアのHtml
 *  @param masterData マスタデータ
 *  @param previousConditionData 前回の検索条件
 */
export interface JobSearchTopProps {
  isLogin: boolean;
  jobCount: JobCountResponse;
  linkAreaHtml: string | null;
  masterData: {
    occupation: JobTypeLResponse[];
    area: AreaResponse[];
    route: CompanyOfSearchModal[];
    industry: IndustryLResponse[];
  };
  previousConditionData: SearchConditionLabelResponse | null;
  initialConditions: InitialConditionsType;
}

export const JobSearchTopScreen: FC<JobSearchTopProps> = ({
  isLogin,
  jobCount,
  linkAreaHtml,
  masterData,
  previousConditionData,
  initialConditions,
}) => {
  useAnalyticsSetup();
  useCmsMemberInfo('pcHeader');

  const validationErrorInitialState: ValidationErrorType = {
    isErrorNonCondition: false,
    isErrorConditionUpperLimit: false,
  };
  const [validationError, setValidationError] = useState<ValidationErrorType>(
    validationErrorInitialState
  );

  // 駅・路線マスタ
  const [routeMasterData, setRouteMasterData] = useState(masterData.route);

  const {
    updateJobCount,
    jobCountData,
    occupation,
    location,
    routeStation,
    industry,
    employmentStatus,
    income,
    pickUpCondition,
    includeSearchKeyword,
    excludeSearchKeyword,
    selectedCurrentCondition,
    searchDetail,
    updateModalJobCount,
    modalTotalCount,
    onClickOccupationModalSettingButton,
    onClickLocationModalSettingButton,
    onClickRouteStationModalSettingButton,
    onClickIndustryModalSettingButton,
    allClearSelectedCondition,
  } = useSearchCondition({
    initialJobCountData: jobCount,
    masterData: {
      occupationMasterData: masterData.occupation,
      areaMasterData: masterData.area,
      routeMasterData,
      industryMasterData: masterData.industry,
    },
    setValidationError,
    initialConditions,
  });

  const {
    holidayAndWayOfWorking,
    requirements,
    workplaceEnvironment,
    welfareBenefits,
    languageSkill,
    positions,
    employeeAverageAge,
    employNum,
    establish,
    jobOfferKind,
  } = searchDetail;

  const { updateIsCheckedHolidayAndWayOfWorkingList } = holidayAndWayOfWorking;
  const { updateIsCheckedRequirementList } = requirements;

  const countUpRef = useCountUpdate(jobCountData.totalCount ?? 0);
  const countUpRefOnNavigation = useCountUpdate(jobCountData.totalCount ?? 0);

  const {
    pickUpConditionDisplayList,
    updateIsCheckedIsNearDeadline,
    updateIsCheckedIsNewArrival,
  } = pickUpCondition;

  const overWriteModal = useDisclosure();
  const locationModal = useDisclosure();

  const [isShowBalloon, setIsShowBallon] = useState<boolean>(false);
  const balloonRef: MutableRefObject<HTMLDivElement | null> = useRef(null);
  const { isIncomeRangeError } = income;

  // バルーン表示時、外側押下で非表示
  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (!balloonRef.current?.contains(e.target as Node)) {
        setIsShowBallon(false);
      }
    };

    document.addEventListener('click', onClickOutside);
    return () => {
      document.removeEventListener('click', onClickOutside);
    };
  }, [setIsShowBallon]);

  // AA計測
  const dispatch = useAppDispatch();
  const hasAdobeAnalyticsScripts = useAppSelector(
    adobeAnalyticsScriptsSelector
  );

  // 「勤務地を選択」押下処理
  const onClickLocationModal = () => {
    locationModal.onOpen();
    measureOnClickLocation(hasAdobeAnalyticsScripts, dispatch);
  };

  // 「路線・駅を選択」押下処理
  const onClickStationModal = () => {
    measureOnClickStation(hasAdobeAnalyticsScripts, dispatch);
  };

  return (
    <>
      <Tag
        title="求人検索 詳細条件から探す － 転職ならdoda（デューダ）"
        description="求人情報の検索。職種、業種、エリアなどさまざまな条件から、大手・優良企業を中心に豊富な求人情報から最適な求人を求人検索できます。"
        keywords="求人検索,転職,求人,求人情報"
        type={OGP_TYPE}
        url={OGP_URL_JOB_SEARCH_TOP}
        image={OGP_IMAGE}
        siteName={OGP_SITE}
        canonical={CANONICAL_URL_JOB_SEARCH_TOP}
      />
      <WithoutNavigation loginStatus totalCount={countUpRefOnNavigation ?? 0} />
      <Breadcrumb>
        <BreadcrumbItemTop />
        <BreadcrumbItem>求人情報を詳細条件から探す</BreadcrumbItem>
      </Breadcrumb>
      <JobSearchTopHeader jobCount={countUpRef ?? 0} />
      {Object.values(validationError).includes(true) && (
        <ValidationErrorText validationError={validationError} />
      )}
      <Box
        color="white"
        radius="4"
        boxShadow="blue"
        className={styles.jobSearchTop__formWrap}
      >
        <div className={styles.jobSearchTop__conditionFormTop}>
          {previousConditionData && (
            <PreviousCondition previousConditionData={previousConditionData} />
          )}
          <SearchConditionSettingForm
            selectedCurrentCondition={selectedCurrentCondition}
            updateJobCount={updateJobCount}
            conditionHooksFunctions={{
              occupation,
              location,
              routeStation,
              industry,
              employmentStatus,
              income,
              pickUpCondition: {
                pickUpConditionDisplayList,
                updateIsCheckedIsNewArrival,
                updateIsCheckedIsNearDeadline,
                updateIsCheckedHolidayAndWayOfWorkingList,
                updateIsCheckedRequirementList,
              },
              includeKeyword: includeSearchKeyword,
              excludeKeyword: excludeSearchKeyword,
            }}
            onClickModalSettingButtonFunctions={{
              onClickOccupationModalSettingButton,
              onClickLocationModalSettingButton,
              onClickRouteStationModalSettingButton,
              onClickIndustryModalSettingButton,
            }}
            updateModalJobCount={updateModalJobCount}
            modalFunctions={{
              locationModal,
            }}
            onClickOpenModalFunctions={{
              onClickLocationModal,
              onClickStationModal,
            }}
            modalTotalCount={modalTotalCount}
            masterData={{
              occupation: masterData.occupation,
              area: masterData.area,
              industry: masterData.industry,
            }}
            onRailwayMasterFetch={(fetchedRailwayMaster) => {
              setRouteMasterData(assignPrefIdList(fetchedRailwayMaster));
            }}
          />
        </div>
        <div className={styles.jobSearchTop__btnWrap}>
          <SearchButton
            onClickSearch={() =>
              moveToSearchResultPage({
                selectedCurrentCondition,
                isIncomeRangeError,
                setValidationError,
              })
            }
            onClickClear={allClearSelectedCondition}
          />
          <SavedButton
            onClick={() =>
              onClickSavedCondition({
                selectedCurrentCondition,
                isIncomeRangeError,
                isLogin,
                overWriteModal,
                setValidationError,
                setIsShowBallon,
              })
            }
            isShowBalloon={isShowBalloon}
            balloonRef={balloonRef}
          />
        </div>
        <div className={styles.jobSearchTop__conditionFormBottom}>
          <SearchSelectiveSettingForm
            holidayAndWayOfWorking={holidayAndWayOfWorking}
            requirements={requirements}
            workplaceEnvironment={workplaceEnvironment}
            welfareBenefits={welfareBenefits}
            languageSkill={languageSkill}
            positions={positions}
            employeeAverageAge={employeeAverageAge}
            employNum={employNum}
            establish={establish}
            jobOfferKind={jobOfferKind}
            updateJobCount={updateJobCount}
          />
        </div>
        <div className={styles.jobSearchTop__btnWrap}>
          <SearchButton
            onClickSearch={() =>
              moveToSearchResultPage({
                selectedCurrentCondition,
                isIncomeRangeError,
                setValidationError,
              })
            }
            onClickClear={allClearSelectedCondition}
          />
          <SavedButton
            onClick={() =>
              onClickSavedCondition({
                selectedCurrentCondition,
                isIncomeRangeError,
                isLogin,
                overWriteModal,
                setValidationError,
                setIsShowBallon,
              })
            }
            isShowBalloon={isShowBalloon}
            balloonRef={balloonRef}
          />
        </div>
      </Box>
      {linkAreaHtml && (
        <div className={styles.jobSearchTop__linkAreaWrap}>
          <ConditionLinkList html={linkAreaHtml} />
        </div>
      )}
      <ModalOverlay
        onClickOverlay={overWriteModal.onClose}
        show={overWriteModal.isOpen}
      >
        <OverwriteConditionModal
          onClickClose={overWriteModal.onClose}
          conditions={createSaveConditionsRequest(selectedCurrentCondition)}
          redirectUrl={getErrorScreenPath()}
        />
      </ModalOverlay>
    </>
  );
};
